import React from 'react';
import { Link } from 'react-router-dom';
import { Mail, Twitter, Linkedin, Github } from 'lucide-react';
import logoDark from '../../assets/brand/logo-dark.png';

// Footer is always navy regardless of the page's light/dark toggle -
// branding-resources/RUDVAN_Brand_Guidelines_v3.docx lists "Midnight navy"
// usage as "Backgrounds, headers, footer, website", and the dark-mode logo
// variant's baked-in background (no alpha channel - see LandingHeader.jsx)
// only reads correctly on a navy background, so this also avoids a visible
// seam without any conditional logic tied to the toggle.
//
// Four-column structure (Brand+social / Product / Company / Legal) matches
// www.rudvan.online's footer per explicit user request (2026-08-07). Links
// point at real anchors on the homepage where they exist (Product, and
// About/Why RUDVAN/For Learners under Company) - pathed as "/login#..."
// (not bare "#...") since this footer renders on nearly every page, not
// just the homepage: a plain <a> to a path+hash does a same-document
// instant scroll when already on /login and a full navigation to /login
// followed by a native scroll-to-hash otherwise (see LandingHeader.jsx's
// NAV_LINKS comment - same bug, same fix, reported live 2026-08-24 from
// /our-team). The Legal column and the social icons mirror that site's
// links, but this app has no Privacy/Terms/Refund/Security pages and no
// confirmed RUDVAN social accounts yet, so those are inert placeholders
// (href="#") rather than real destinations - flagged here so it's easy to
// wire up once those pages/accounts exist.
const PRODUCT_LINKS = [
  { href: '/login#features', label: 'Features' },
  { href: '/login#how-it-works', label: 'How It Works' },
  { href: '/login#demo', label: 'Demo' },
  { href: '/login#pricing', label: 'Pricing' },
];

const COMPANY_LINKS = [
  { href: '/login#about', label: 'About' },
  { href: '/our-team', label: 'Our Team' },
  { href: '/login#comparison', label: 'Why RUDVAN' },
  { href: '/login#target', label: 'For Learners' },
];

const LEGAL_LINKS = [
  { href: '#', label: 'Privacy Policy' },
  { href: '#', label: 'Terms of Service' },
  { href: '#', label: 'Refund Policy' },
  { href: '#', label: 'Security' },
];

const SOCIAL_LINKS = [
  { href: '#', label: 'Email', icon: Mail },
  { href: '#', label: 'Twitter', icon: Twitter },
  { href: '#', label: 'LinkedIn', icon: Linkedin },
  { href: '#', label: 'GitHub', icon: Github },
];

// Shared by nearly every internal page too (AdminPortal, StudentProfile,
// QuizPortal, etc. all render this same component), so a fix here already
// reaches "all internal pages" without touching them individually.
export default function LandingFooter() {
  return (
    <footer className="relative z-10 bg-navy mt-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
        {/* Brand block spans the full row on mobile (col-span-3 of 3),
            with Product/Company/Legal side by side in a row below it
            instead of each stacking on its own line - explicit user
            request to shorten the footer's mobile height ("bring the
            three components side by side", docs/DECISIONS.md, "Landing
            page mobile layout" decision). md:col-span-1 + md:grid-cols-4
            restores the original 4-equal-column desktop layout exactly. */}
        <div className="grid grid-cols-3 md:grid-cols-4 gap-4 sm:gap-8 mb-6 sm:mb-8">
          <div className="col-span-3 md:col-span-1">
            <img src={logoDark} alt="RUDVAN - Adapt. Practice. Master." className="h-12 w-auto mb-4" />
            <p className="text-white/60 text-sm max-w-xs mb-4">
              AI-powered adaptive learning platform for IIT-JEE and NEET foundation learners.
            </p>
            <div className="flex items-center gap-3">
              {SOCIAL_LINKS.map(({ href, label, icon: Icon }) => (
                <a
                  key={label}
                  href={href}
                  aria-label={label}
                  className="p-2 rounded-btn bg-white/10 text-white/70 hover:bg-white/20 hover:text-white transition"
                >
                  <Icon size={16} />
                </a>
              ))}
            </div>
          </div>
          <div>
            <h4 className="font-bold text-white text-sm sm:text-base mb-2 sm:mb-4">Product</h4>
            <ul className="space-y-1.5 sm:space-y-2 text-xs sm:text-sm text-white/60">
              {PRODUCT_LINKS.map((link) => (
                <li key={link.href}>
                  {link.href.includes('#') ? (
                    <a href={link.href} className="hover:text-brand-cyan transition">{link.label}</a>
                  ) : (
                    <Link to={link.href} className="hover:text-brand-cyan transition">{link.label}</Link>
                  )}
                </li>
              ))}
            </ul>
          </div>
          <div>
            <h4 className="font-bold text-white text-sm sm:text-base mb-2 sm:mb-4">Company</h4>
            <ul className="space-y-1.5 sm:space-y-2 text-xs sm:text-sm text-white/60">
              {COMPANY_LINKS.map((link) => (
                <li key={link.href}>
                  {link.href.includes('#') ? (
                    <a href={link.href} className="hover:text-brand-cyan transition">{link.label}</a>
                  ) : (
                    <Link to={link.href} className="hover:text-brand-cyan transition">{link.label}</Link>
                  )}
                </li>
              ))}
            </ul>
          </div>
          <div>
            <h4 className="font-bold text-white text-sm sm:text-base mb-2 sm:mb-4">Legal</h4>
            <ul className="space-y-1.5 sm:space-y-2 text-xs sm:text-sm text-white/60">
              {LEGAL_LINKS.map((link) => (
                <li key={link.label}><a href={link.href} className="hover:text-brand-cyan transition">{link.label}</a></li>
              ))}
            </ul>
          </div>
        </div>
        <div className="border-t border-white/10 pt-6 sm:pt-8 flex flex-col md:flex-row justify-between items-center gap-2">
          <p className="text-white/50 text-xs sm:text-sm text-center">© 2026 RUDVAN. All rights reserved. Built with ❤️ for learners.</p>
          <p className="text-white/50 text-xs sm:text-sm">Adapt. Practice. Master.</p>
        </div>
      </div>
    </footer>
  );
}
